// src/components/Header.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import OptimizedImage from './OptimizedImage';
import Iridescence from './Iridescence'; // Ahora sí existe el archivo
import LogoAlgorithmia from '../assets/LOGOAZUL-Ajpg.jpg'; // Ruta corregida
import ContactForm from './ContactForm';
import MeetingRequestForm from './MeetingRequestForm';

// Definimos las props que nuestro Header puede recibir
interface HeaderProps {
  isSplashLoading?: boolean; // Para saber si el splash screen sigue activo y retrasar la animación
}

const Header: React.FC<HeaderProps> = ({ isSplashLoading = true }) => {
  const navigate = useNavigate();
  // Estos estados controlan los modales.
  const [showContactModal, setShowContactModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  // Efecto para escuchar eventos personalizados desde otras páginas
  React.useEffect(() => {
    const handleOpenContact = () => setShowContactModal(true);
    const handleOpenMeeting = () => setShowMeetingModal(true);
    
    window.addEventListener('openContactModal', handleOpenContact);
    window.addEventListener('openMeetingModal', handleOpenMeeting);
    
    return () => {
      window.removeEventListener('openContactModal', handleOpenContact);
      window.removeEventListener('openMeetingModal', handleOpenMeeting);
    };
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: 0,
          opacity: 1,
          transition: { delay: isSplashLoading ? 0.8 : 0 }
        }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="fixed w-full z-40 py-4 px-4 sm:px-8 md:px-12 lg:px-24 bg-white/10 backdrop-blur-lg shadow-lg border-b border-white/20 overflow-hidden"
        style={{
          width: '100vw',
          left: 0,
          right: 0,
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
        }}
      >
        <div className="absolute inset-0 iridescence-container">
          <Iridescence
            color={[0.29, 0.87, 0.51]}
            speed={0.8}
            amplitude={0.15}
            mouseReact={true}
            className="opacity-80"
          />
        </div>

        <div className="relative z-10 flex justify-between items-center">
          {/* Logo que navega al Inicio */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center"
          >
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <motion.div
                className="h-8 w-8 sm:h-10 sm:w-10 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-lg flex items-center justify-center shadow-lg"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <OptimizedImage
                  src={LogoAlgorithmia}
                  alt="Algorithmia - Desarrollo de Software Córdoba"
                  className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                />
              </motion.div>
              <div className="flex flex-col">
                <h1 className="text-lg sm:text-xl font-bold text-white whitespace-nowrap">
                  Algorithmia
                </h1>
                <p className="text-xs text-white/70 hidden sm:block">
                  Intelligent Logic. Digital Solutions.
                </p>
              </div>
            </Link>
          </motion.div>

          {/* Navegación principal con Links de React Router */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/servicios"
              className="text-white font-semibold relative py-2 hover:text-[#4ADE80] transition-colors duration-200"
            >
              <span className="relative">
                Servicios
                <motion.div
                  className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-[#4ADE80] to-[#3BC571]"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </span>
            </Link>
            <Link
              to="/proyectos"
              className="text-white font-semibold relative py-2 hover:text-[#4ADE80] transition-colors duration-200"
            >
              <span className="relative">
                Proyectos
                <motion.div
                  className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-[#4ADE80] to-[#3BC571]"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </span>
            </Link>
            <Link
              to="/tecnologia"
              className="text-white font-semibold relative py-2 hover:text-[#4ADE80] transition-colors duration-200"
            >
              <span className="relative">
                Tecnología
                <motion.div
                  className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-[#4ADE80] to-[#3BC571]"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </span>
            </Link>
            <Link
              to="/contacto"
              className="text-white font-semibold relative py-2 hover:text-[#4ADE80] transition-colors duration-200"
            >
              <span className="relative">
                Contacto
                <motion.div
                  className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-[#4ADE80] to-[#3BC571]"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </span>
            </Link>
          </div>

          {/* Botón Contactar que abre el modal */}
          <motion.button
            whileHover={{
              scale: 1.05,
              boxShadow: "0 5px 15px rgba(74, 222, 128, 0.4)"
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowContactModal(true)}
            className="px-4 py-2 sm:px-6 sm:py-2 md:px-8 md:py-3 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden border-2 border-[#4ADE80]/50 hover:border-[#3BC571] text-sm sm:text-base group"
          >
            <span className="relative z-10 drop-shadow-sm whitespace-nowrap">Contactar</span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          </motion.button>
        </div>
      </motion.header>

      {/* Modales de Contacto */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[99999] p-5"
            onClick={() => setShowContactModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto relative shadow-2xl border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white rounded-full w-9 h-9 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#3BC571] hover:to-[#2AAE60] transition-all duration-200 z-50 shadow-md"
                onClick={() => setShowContactModal(false)}
              >
                ×
              </button>
              <ContactForm onClose={() => setShowContactModal(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modales de Reunión */}
      <AnimatePresence>
        {showMeetingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[99999] p-5"
            onClick={() => setShowMeetingModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto relative shadow-2xl border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white rounded-full w-9 h-9 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#3BC571] hover:to-[#2AAE60] transition-all duration-200 z-50 shadow-md"
                onClick={() => setShowMeetingModal(false)}
              >
                ×
              </button>
              <MeetingRequestForm onClose={() => setShowMeetingModal(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;